import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import gettext from 'js-utils/gettext';
import classNames from 'classnames';
import { logCheckpointViaAjax } from 'js-utils/janus';
import find from 'lodash/find';
import filter from 'lodash/filter';

import {
    SIZE_BLOCK,
    STYLE_FILL,
    STYLE_LINK,
    STYLE_NEUTRAL,
} from 'eventbrite_design_system/button/constants';

import Tag from 'eventbrite_design_system/tag/Tag';
import Button from 'eventbrite_design_system/button/Button';

import withFocusDrawerControls from 'eventbrite_design_system/structure/hoc/withFocusDrawerControls';

import DropdownFilter from './DropdownFilter';
import DateFilter from './DateFilter';
import { formatDateRangeStringForDisplay } from '../utils';
import {
    CUSTOM_DATE,
    CATEGORY_FILTER,
    DATE_FILTER,
    FORMAT_FILTER,
    PRICE_FILTER,
    FACET_FILTER,
    TAG_BACKGROUND_COLOR,
    GA_SEARCH_TAG_CLICK_ACTION,
    GA_SEARCH_TAG_CLEAR_ACTION,
    GA_MORE_FILTERS_PANEL_ACTION,
    GA_MORE_FILTERS_CLEARED_ACTION,
    GA_SEARCH_CATEGORY,
    SEARCH_TAG_CLICKED,
    SEARCH_TAG_CLEARED,
    FACETED_SEARCH_DROPDOWN_FILTER_PROP_MAP,
    CLEAR_PRICE_FILTER,
} from '../constants';

import './facetedSearchFilters.scss';

const getRenderedFilters = (format, price, category, onClose) => {
    const filterObject = {
        [CATEGORY_FILTER]: category,
        [FORMAT_FILTER]: format,
        [PRICE_FILTER]: price,
    };

    const appliedFilters = filter(
        Object.keys(filterObject),
        (key) => filterObject[key],
    );

    return appliedFilters.map((key) => {
        // Given an applied filters value, grab the appropriate
        // display value associated to that filter
        const { display } = find(
            FACETED_SEARCH_DROPDOWN_FILTER_PROP_MAP[key].values,
            ({ value }) => value === filterObject[key],
        );

        return (
            <span className="facet eds-l-pad-left-3" key={key}>
                <Tag
                    initialIsActive={false}
                    key={key}
                    isClosable={true}
                    isStatic={true}
                    backgroundColor={TAG_BACKGROUND_COLOR}
                    onClose={onClose.bind(null, key)}
                >
                    {display}
                </Tag>
            </span>
        );
    });
};

const MoreFilters = ({
    handleFilterSelection,
    category = '',
    format = '',
    price = '',
    initialCategory = '',
    initialFormat = '',
    initialPrice = '',
}) => (
    <section className="eds-l-pad-all-4">
        <DropdownFilter
            handleSelect={handleFilterSelection}
            shouldOrderValues={true}
            type={CATEGORY_FILTER}
            value={category}
            isHighlighted={category !== initialCategory}
            {...FACETED_SEARCH_DROPDOWN_FILTER_PROP_MAP.tags}
        />
        <DropdownFilter
            handleSelect={handleFilterSelection}
            shouldOrderValues={true}
            type={FORMAT_FILTER}
            value={format}
            isHighlighted={format !== initialFormat}
            {...FACETED_SEARCH_DROPDOWN_FILTER_PROP_MAP.format}
        />
        <DropdownFilter
            handleSelect={handleFilterSelection}
            shouldOrderValues={true}
            type={PRICE_FILTER}
            value={price}
            isHighlighted={
                price !== initialPrice && price !== CLEAR_PRICE_FILTER
            }
            {...FACETED_SEARCH_DROPDOWN_FILTER_PROP_MAP.price}
        />
    </section>
);

const BottomBarContent = ({
    closeFocusDrawer,
    handleApplyFilters,
    shouldShowApplyActiveButton,
}) => (
    <div className="eds-l-pad-all-4 eds-align--center">
        <div className="eds-g-cell eds-g-cell-1-1 eds-l-pad-right-2">
            <Button onClick={closeFocusDrawer} size={SIZE_BLOCK}>
                {gettext('Close')}
            </Button>
        </div>
        <div className="eds-g-cell eds-g-cell-1-1 eds-l-pad-left-2">
            <Button
                onClick={handleApplyFilters}
                size={SIZE_BLOCK}
                style={shouldShowApplyActiveButton ? STYLE_FILL : null}
            >
                {gettext('Apply')}
            </Button>
        </div>
    </div>
);
const EnhancedBottomBarContent = withFocusDrawerControls(BottomBarContent);

const MoreFiltersToggle = ({ onClick, spacingClass }) => {
    const btnProps = {
        onClick,
        style: STYLE_NEUTRAL,
        size: SIZE_BLOCK,
    };

    return (
        <div className={`${spacingClass} more-filters`}>
            <Button {...btnProps}>
                <span className="eds-text-bs">{gettext('More filters')}</span>
            </Button>
        </div>
    );
};

export class FacetedSearchFilters extends PureComponent {
    static propTypes = {
        trackGAEvent: PropTypes.func,
        handleApplyFilter: PropTypes.func,
        handleRemoveFilter: PropTypes.func,
        selectedCategory: PropTypes.string,
        selectedPrice: PropTypes.string,
        selectedDate: PropTypes.string,
        selectedFormat: PropTypes.string,
        isBotRequest: PropTypes.bool,
        locationSlug: PropTypes.string,
        facets: PropTypes.arrayOf(
            PropTypes.shape({
                key: PropTypes.string,
                count: PropTypes.number,
                displayName: PropTypes.string,
            }),
        ),
        isLoading: PropTypes.bool,

        showFocusDrawer: PropTypes.func,
        closeFocusDrawer: PropTypes.func,
        addFocusDrawerNotification: PropTypes.func,
        hideFocusDrawerNotification: PropTypes.func,
    };

    constructor(props) {
        super(props);

        const {
            selectedCategory,
            selectedDate,
            selectedFormat,
            selectedPrice,
        } = props;

        this.state = {
            hasMoreFilters: !!(
                selectedCategory ||
                selectedFormat ||
                selectedPrice
            ),
            shouldShowPicker: false,
            [DATE_FILTER]: selectedDate,
            [CATEGORY_FILTER]: selectedCategory,
            [FORMAT_FILTER]: selectedFormat,
            [PRICE_FILTER]: selectedPrice,
            [`initial${CATEGORY_FILTER}`]: selectedCategory,
            [`initial${FORMAT_FILTER}`]: selectedFormat,
            [`initial${PRICE_FILTER}`]: selectedPrice,
        };
    }

    // eslint-disable-next-line camelcase
    UNSAFE_componentWillReceiveProps({
        selectedCategory: newCategory,
        selectedPrice: newPrice,
        selectedDate: newDate,
        selectedFormat: newFormat,
    }) {
        this.setState(
            ({
                [DATE_FILTER]: currentDate,
                [PRICE_FILTER]: currentPrice,
                [CATEGORY_FILTER]: currentCategory,
                [FORMAT_FILTER]: currentFormat,
            }) => {
                let price = currentPrice;

                let category = currentCategory;

                let date = currentDate;

                let format = currentFormat;

                if (currentDate !== newDate) {
                    date = newDate;
                }

                if (currentCategory !== newCategory) {
                    category = newCategory;
                }

                if (currentPrice !== newPrice) {
                    price = newPrice;
                }

                if (currentFormat !== newFormat) {
                    format = newFormat;
                }

                return {
                    [DATE_FILTER]: date,
                    [PRICE_FILTER]: price,
                    [CATEGORY_FILTER]: category,
                    [FORMAT_FILTER]: format,
                    [`initial${CATEGORY_FILTER}`]: category,
                    [`initial${FORMAT_FILTER}`]: format,
                    [`initial${PRICE_FILTER}`]: price,
                    hasMoreFilters: price || category || format,
                };
            },
        );
    }

    _setRef = (node) => {
        this._facetContainer = node;
    };

    _handleApplyFilter(filterType, selectedFilter) {
        const { handleApplyFilter } = this.props;

        if (filterType === DATE_FILTER && selectedFilter === CUSTOM_DATE) {
            return this.setState({ shouldShowPicker: true });
        }

        return this.setState({ [filterType]: selectedFilter }, () => {
            handleApplyFilter({ [filterType]: selectedFilter });
        });
    }

    _handleRemoveFilter(filterType) {
        const { handleRemoveFilter } = this.props;

        this.setState({ [filterType]: '' }, () => {
            handleRemoveFilter({ [filterType]: true });
        });
    }

    _onHandleApplyMoreFilters = () => {
        const { closeFocusDrawer, handleApplyFilter } = this.props;
        const { format, price, tags } = this.state;

        this.setState(
            {
                hasMoreFilters: format || price || tags,
            },
            () => {
                handleApplyFilter({ format, price, tags });
                closeFocusDrawer();
            },
        );
    };

    _handleDatePickerBlur = ({ startDate, endDate }) => {
        const { handleApplyFilter } = this.props;

        if (startDate && endDate) {
            this.setState(
                {
                    shouldShowPicker: false,
                    [DATE_FILTER]: formatDateRangeStringForDisplay({
                        startDate,
                        endDate,
                    }),
                },
                () => {
                    handleApplyFilter(
                        { [DATE_FILTER]: CUSTOM_DATE },
                        { startDate, endDate },
                    );
                },
            );
        } else {
            this._handleCancelDatePicker();
        }
    };

    _handleCancelDatePicker = () => {
        this.setState({
            shouldShowPicker: false,
        });
    };

    _handleShowSideDrawer = () => {
        const { showFocusDrawer, trackGAEvent } = this.props;
        const { hasMoreFilters } = this.state;

        trackGAEvent({
            category: GA_SEARCH_CATEGORY,
            action: GA_MORE_FILTERS_PANEL_ACTION,
        });

        /* Drawer Component Instantiation */

        // Redefining these components within the _handleShowSideDrawer call to
        // ensure that they are re-rendered with the most up to date state. As otherwise
        // if state changes occur while the drawer is closed, the interal components
        // will not update as expected. This is an understood negative performance
        // impact, and if we move forward with this component beyond the experiment,
        // we will work towards achieving a better solution.
        const TitleNode = () => {
            let Component = <span>{gettext('More filters')}</span>;

            if (hasMoreFilters) {
                Component = (
                    <div className="faceted-search-filters__drawer__title">
                        <div className="faceted-search-filters__drawer__title--header">
                            {gettext('More filters')}
                        </div>
                        <div className="eds-text-bm eds-l-pad-right-3">
                            <Button
                                onClick={this._handleClearMoreFilters}
                                style={STYLE_LINK}
                            >
                                {gettext('Clear filters')}
                            </Button>
                        </div>
                    </div>
                );
            }

            return Component;
        };

        const MoreFiltersWrapper = () => {
            const {
                [CATEGORY_FILTER]: category,
                [FORMAT_FILTER]: format,
                [PRICE_FILTER]: price,
                [`initial${CATEGORY_FILTER}`]: initialCategory,
                [`initial${FORMAT_FILTER}`]: initialFormat,
                [`initial${PRICE_FILTER}`]: initialPrice,
            } = this.state;

            return (
                <MoreFilters
                    category={category}
                    format={format}
                    price={price}
                    initialCategory={initialCategory}
                    initialFormat={initialFormat}
                    initialPrice={initialPrice}
                    handleFilterSelection={this._onHandleFilterSelection}
                />
            );
        };

        /* Drawer Component Instantiation */

        showFocusDrawer({
            bottomBarIsShown: true,
            hideClose: true,
            onClickOutside: this._onHandleApplyMoreFilters,
            content: <MoreFiltersWrapper />,
            title: <TitleNode />,
            bottomBarOptions: {
                isShown: true,
                barContent: (
                    <EnhancedBottomBarContent
                        handleApplyFilters={this._onHandleApplyMoreFilters}
                    />
                ),
            },
        });
    };

    _handleClearMoreFilters = () => {
        const {
            closeFocusDrawer,
            handleRemoveFilter,
            trackGAEvent,
        } = this.props;

        trackGAEvent({
            category: GA_SEARCH_CATEGORY,
            action: GA_MORE_FILTERS_CLEARED_ACTION,
        });

        this.setState(
            {
                hasMoreFilters: false,
                [CATEGORY_FILTER]: '',
                [FORMAT_FILTER]: '',
                [PRICE_FILTER]: '',
            },
            () => {
                handleRemoveFilter({
                    [CATEGORY_FILTER]: true,
                    [FORMAT_FILTER]: true,
                    [PRICE_FILTER]: true,
                });
                closeFocusDrawer();
            },
        );
    };

    _onHandleFilterSelection = (filterType, selectedFilter) => {
        const { showFocusDrawerBottomBar } = this.props;

        this.setState(
            {
                [filterType]: selectedFilter,
            },
            () => {
                const {
                    [CATEGORY_FILTER]: category = '',
                    [FORMAT_FILTER]: format = '',
                    [PRICE_FILTER]: price = '',
                    [`initial${CATEGORY_FILTER}`]: initialCategory = '',
                    [`initial${FORMAT_FILTER}`]: initialFormat = '',
                    [`initial${PRICE_FILTER}`]: initialPrice = '',
                } = this.state;

                const shouldShowApplyActiveButton =
                    category !== initialCategory ||
                    format !== initialFormat ||
                    price !== initialPrice;

                showFocusDrawerBottomBar({
                    barContent: (
                        <EnhancedBottomBarContent
                            handleApplyFilters={this._onHandleApplyMoreFilters}
                            shouldShowApplyActiveButton={
                                shouldShowApplyActiveButton
                            }
                        />
                    ),
                });
            },
        );
    };

    _handleFacetClick = (facetKey) => {
        const { handleApplyFilter, trackGAEvent } = this.props;

        // Move the container all the way to
        // left when the user selects it
        if (this._facetContainer) {
            this._facetContainer.scrollLeft = 0;
        }

        trackGAEvent({
            category: GA_SEARCH_CATEGORY,
            action: GA_SEARCH_TAG_CLICK_ACTION,
            label: facetKey,
        });

        logCheckpointViaAjax(SEARCH_TAG_CLICKED);

        this._handleFacetFilterChange(facetKey, handleApplyFilter);
    };

    _handleFacetRemoval = (facetKey) => {
        const { handleRemoveFilter, trackGAEvent } = this.props;

        trackGAEvent({
            category: GA_SEARCH_CATEGORY,
            action: GA_SEARCH_TAG_CLEAR_ACTION,
            label: facetKey,
        });

        logCheckpointViaAjax(SEARCH_TAG_CLEARED);

        this._handleFacetFilterChange(facetKey, handleRemoveFilter);
    };

    _handleFacetFilterChange = (key, filterAction) => {
        filterAction({ [FACET_FILTER]: key });
    };

    render() {
        const {
            isBotRequest,
            locationSlug,
            isLoading,
            selectedCategory,
            selectedPrice,
            selectedFormat,
        } = this.props;
        const { shouldShowPicker, [DATE_FILTER]: date } = this.state;

        const classes = classNames(
            'faceted-search-filters',
            'faceted-search-filters--old-search',
            'eds-l-pad-left-6',
            'eds-l-pad-right-6',
            // always show them when it's a bot
            { 'eds-show': isBotRequest },
        );
        const scrollablePaneClasses = classNames(
            'faceted-search-filters__facets eds-l-pad-hor-2 eds-l-pad-bot-3',
            {
                'faceted-search-filters__facets--picker-open': shouldShowPicker,
            },
        );

        const appliedFiltersAsFacets = getRenderedFilters(
            selectedFormat,
            selectedPrice,
            selectedCategory,
            this._handleRemoveFilter.bind(this),
        );

        const allFacets = [...appliedFiltersAsFacets];

        return (
            <section className={classes} data-spec="faceted-search-filters">
                <div className="date-filter faceted-search-filters__gradient--right">
                    <div />
                </div>
                <div className={scrollablePaneClasses} ref={this._setRef}>
                    <DateFilter
                        type={DATE_FILTER}
                        handleSelect={this._handleApplyFilter.bind(this)}
                        value={date}
                        locationSlug={locationSlug}
                        handleClear={this._handleRemoveFilter.bind(this)}
                        shouldShowPicker={shouldShowPicker}
                        onDatePickerBlur={this._handleDatePickerBlur}
                        onCancelPicker={this._handleCancelDatePicker}
                        isBotRequest={isBotRequest}
                        isLoading={isLoading}
                    />
                    {allFacets}
                    <MoreFiltersToggle
                        onClick={this._handleShowSideDrawer}
                        spacingClass="eds-l-pad-hor-3 eds-show-down-sw"
                    />
                </div>
                <div className="faceted-search-filters__pinned-more-filters faceted-search-filters__gradient--left">
                    <div />
                    <MoreFiltersToggle
                        onClick={this._handleShowSideDrawer}
                        spacingClass="eds-l-pad-left-3 eds-show-up-mn"
                    />
                </div>
            </section>
        );
    }
}

export default withFocusDrawerControls(FacetedSearchFilters);
